/**
 * Copy for the public `/sso-error` page - kept out of the .jsx so it is a pure, importable
 * module: the suite runs on `testEnvironment: 'node'` with `transform: {}`, so a component
 * cannot be rendered in a unit test but a plain table still can.
 *
 * Every reason `api/controllers/ssoController.js` can redirect with gets its OWN sentence AND
 * its own next step (card 54620). `agency_mismatch` was missing before this: the controller has
 * emitted it since card 28212, and the page answered it with the generic "unexpected error"
 * copy, which told the reader nothing they could act on.
 *
 * The next step is separate from the message on purpose - the message says WHAT went wrong, to
 * an agent who cannot read a stack trace; the next step says WHO fixes it and HOW.
 */
export const SSO_ERROR_COPY = {
  invalid_request: {
    message: 'The sign-in link was incomplete - some of the required information was missing from it.',
    nextStep: 'Ask your agency administrator to send you a fresh sign-in link and open it directly from that message.'
  },
  invalid_signature: {
    message: 'The sign-in link could not be verified. Its token did not match the signature your agency should have sent.',
    nextStep: 'Do not retry this link. Ask your agency administrator to confirm the SSO secret is configured correctly, then issue a new link.'
  },
  expired_timestamp: {
    message: 'The sign-in link has expired. Single sign-on links are only valid for five minutes.',
    nextStep: 'Ask your agency administrator to generate a new link and open it straight away.'
  },
  agency_not_found: {
    message: 'No agency matches the agency code in this sign-in link.',
    nextStep: 'Contact your agency administrator to confirm your agency is registered with Oval, then ask them for a new link.'
  },
  sso_disabled: {
    message: 'Single sign-on is not enabled for your agency yet.',
    nextStep: 'Contact your agency administrator to enable SSO, or sign in with your email address and password.'
  },
  agency_mismatch: {
    message: 'This account is not registered under the agency that sent the sign-in link.',
    nextStep: 'Ask your agency administrator to confirm which agency your account belongs to, or sign in with your email address and password.'
  }
};

/**
 * What an unrecognised (or absent) reason renders. It never names the code, never repeats any
 * part of the query string, and still gives the reader somewhere to go.
 */
export const GENERIC_COPY = {
  message: 'We could not complete single sign-on.',
  nextStep: 'Sign in with your email address and password instead. If the problem continues, contact support and tell them the time you saw this page.'
};

/**
 * Resolve a raw `?reason=` value to the copy to render.
 *
 * The lookup is an OWN-PROPERTY check: a reason like `constructor` or `toString` otherwise
 * resolves to an Object.prototype member and React tries to render a function as a child.
 * `known` is the VALIDATED code (or null), so a caller can print a trace reference only for a
 * code from this fixed vocabulary and never echo arbitrary query text onto the page.
 */
export const resolveSsoErrorCopy = (reason) => {
  const known = typeof reason === 'string'
    && Object.prototype.hasOwnProperty.call(SSO_ERROR_COPY, reason)
    ? reason
    : null;

  return { ...(known ? SSO_ERROR_COPY[known] : GENERIC_COPY), known };
};

export default { SSO_ERROR_COPY, GENERIC_COPY, resolveSsoErrorCopy };
